import { enumSchema } from '@shared/utils/enum.utils';

import { LabourCostMethod } from '../enums/labour-cost-method.enum';
import { getMoneySchema, Money } from '../utils/money.model';

export interface ProductPackage {
  id: string;
  projectId?: string;
  code?: string;
  name: string;
  unitOfMeasure?: string;
  quantity?: number;
  items: ProductPackageItem[];
  linkedPackageIds?: string[];

  // summary fields
  itemCount?: number;
  productsSummary?: string;
  linkedPackages?: ProductPackageSummary[];
  allCombinedItems?: ProductPackageItem[]; // INFO (Denis) - All the items combined from all linked packages + current
  isBasePackage?: boolean;
  isProjectAssignment?: boolean;
}

export interface ProductPackageSummary {
  id: string;
  code?: string;
  name: string;
  itemCount?: number;
  productsSummary?: string;
  allCombinedItems?: ProductPackageItem[]; // INFO (Denis) - All the items combined from all linked packages + current
}

export interface ProductPackageItem {
  id: string;
  productTemplateId?: string;
  referenceCode?: string;
  name?: string;
  quantity: number;
  phaseTemplateId?: string;

  // INFO (Denis) - all the fields below are only settable when productTemplateId is not set (i.e. a manually added "virtual" item)
  labourCostMethod?: LabourCostMethod;
  baseSalaryPerUnit?: Money;
  baseUnitTime?: number;
  unitCost?: Money;
  specialTreatmentCosts?: Money;
  additionalCostsPercentage?: number;
  additionalSubcontractorCosts?: Money;
  additionalLabourCostsPercentage?: number;
}

export interface ProductPackageEditItem {
  productId?: string;
  item: ProductPackageItem;
  phaseId: string;
  quantity: number;

  sourcePackageId?: string;
  sourcePackageGuid?: string;
  sourcePackageName?: string;
  sourcePackageCode?: string;
  sourcePackageItemId?: string;
}

export interface ProductPackagesMassEditRow {
  id: string;
  packageId: string;
  packageName: string;
  packageCode?: string;
  sourcePackageName?: string;
  sourcePackageCode?: string;
  referenceCode: string;
  name: string;
  quantity: number;
  phaseId: string;
}

export const productPackageChatbotFields = {
  id: { type: 'string', description: 'Must be specified for update or delete.' },
  code: {
    type: 'string',
    description: 'Must be specified for new entries. Otherwise should be only set if the intend it to change it.',
  },
  name: {
    type: 'string',
    description: 'Must be specified for new entries. Otherwise should be only set if the intend it to change it.',
  },
  unitOfMeasure: { type: 'string' },
  quantity: { type: 'number' },
  items: {
    type: 'array',
    description:
      'The system will take the whole list and set it to the product package. This means you must always return the full list if any item should be changed in the list. If no item should be changed, then the whole items field can be ommited.',
    items: {
      type: 'object',
      properties: {
        id: { type: 'string' },
        productTemplateId: {
          type: 'string',
          description:
            'ID of the product template. Optional. If item is build based on an existing product template then the name, reference code should be copied from the product template.',
        },
        referenceCode: { type: 'string' },
        name: { type: 'string' },
        quantity: { type: 'number' },
        phaseTemplateId: { type: 'string' },
        specialTreatmentCosts: getMoneySchema('Optional. Only allowed when productTemplateId is not set.'),
        labourCostMethod: enumSchema(
          'LabourCostMethod',
          LabourCostMethod,
          'Optional. Only allowed when productTemplateId is not set.'
        ),
        baseSalaryPerUnit: getMoneySchema('Optional. Only allowed when productTemplateId is not set.'),
        baseUnitTime: { type: 'number', description: 'Optional. Only allowed when productTemplateId is not set.' },
        unitCost: getMoneySchema('Optional. Only allowed when productTemplateId is not set.'),
        additionalCostsPercentage: {
          type: 'number',
          description: 'Optional. Only allowed when productTemplateId is not set.',
        },
        additionalSubcontractorCosts: getMoneySchema('Optional. Only allowed when productTemplateId is not set.'),
        additionalLabourCostsPercentage: {
          type: 'number',
          description: 'Optional. Only allowed when productTemplateId is not set.',
        },
      },
    },
  },
};
